import { useState, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ChevronRight } from 'lucide-react';
import type { User } from '@/types';
import api from '@/lib/api';
import { formatTime12Hour, computeRemainingAvailability } from '@/utils/calendarHelpers';
import { WEEKDAYS } from '@/utils/constants';
import type { DayData } from './types';

interface RecurringClaimSectionProps {
  calendarDays: DayData[];
  currentUser: User;
  queryClient: ReturnType<typeof useQueryClient>;
}

export function RecurringClaimSection({ calendarDays, currentUser, queryClient }: RecurringClaimSectionProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedDays, setSelectedDays] = useState<number[]>([]);
  const [startTime, setStartTime] = useState('17:30');
  const [endTime, setEndTime] = useState('19:30');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Find matching slots based on selected days and time
  const matchingSlots = useMemo(() => {
    if (selectedDays.length === 0 || !startTime || !endTime) return [];

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return calendarDays
      .filter((day) => {
        // Must be in the future
        const dayDate = new Date(day.date);
        dayDate.setHours(0, 0, 0, 0);
        if (dayDate < today) return false;

        // Must be a selected day of week
        const dayOfWeek = day.date.getDay();
        if (!selectedDays.includes(dayOfWeek)) return false;

        // Must have availability that overlaps with selected time
        const remainingWindows = computeRemainingAvailability(day.availabilityWindows, day.claimedSlots);
        return remainingWindows.some((w) => {
          return w.start <= startTime && w.end >= endTime;
        });
      })
      .map((day) => ({
        date: day.dateString,
        dayName: format(day.date, 'EEE, MMM d'),
      }));
  }, [calendarDays, selectedDays, startTime, endTime]);

  const toggleDay = (dayValue: number) => {
    setSelectedDays((prev) =>
      prev.includes(dayValue) ? prev.filter((d) => d !== dayValue) : [...prev, dayValue]
    );
    setSuccess(null);
  };

  const handleClaimAll = async () => {
    if (matchingSlots.length === 0) return;

    setIsSubmitting(true);
    setError(null);
    setSuccess(null);

    let successCount = 0;
    let failCount = 0;

    for (const slot of matchingSlots) {
      try {
        const startDatetimeUtc = `${slot.date}T${startTime}:00.000Z`;
        const endDatetimeUtc = `${slot.date}T${endTime}:00.000Z`;

        await api.post('/timeslots', {
          date: slot.date,
          start_time: startTime,
          end_time: endTime,
          start_datetime_utc: startDatetimeUtc,
          end_datetime_utc: endDatetimeUtc,
        });
        successCount++;
      } catch {
        failCount++;
      }
    }

    queryClient.invalidateQueries({ queryKey: ['timeslots'] });
    queryClient.invalidateQueries({ queryKey: ['availability'] });

    if (failCount === 0) {
      setSuccess(`Claimed ${successCount} slots!`);
      setSelectedDays([]);
    } else {
      setError(`Claimed ${successCount} slots, ${failCount} failed.`);
    }

    setIsSubmitting(false);
  };

  return (
    <div className="bg-card rounded-xl border border-slate-700 p-6">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        data-testid="toggle-recurring-claim"
        className="flex items-center justify-between w-full text-left"
      >
        <div>
          <h2 className="text-xl font-semibold text-white">Claim Recurring Slots</h2>
          <p className="text-gray-400 text-sm mt-1">Claim multiple days at once</p>
        </div>
        <ChevronRight
          size={20}
          className={`text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
        />
      </button>

      {isExpanded && (
        <div className="mt-4 space-y-4">
          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
              {error}
            </div>
          )}
          {success && (
            <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg text-emerald-400 text-sm">
              {success}
            </div>
          )}

          {/* Day selection */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Select Days</label>
            <div className="flex flex-wrap gap-2">
              {WEEKDAYS.map((day) => (
                <button
                  key={day.value}
                  onClick={() => toggleDay(day.value)}
                  data-testid={`clickable-recurring-day-${day.value}`}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    selectedDays.includes(day.value)
                      ? 'bg-blue-500 text-white'
                      : 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                  }`}
                >
                  {day.label}
                </button>
              ))}
            </div>
          </div>

          {/* Time selection */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Start Time</label>
              <input
                type="time"
                value={startTime}
                onChange={(e) => {
                  setStartTime(e.target.value);
                  setSuccess(null);
                }}
                data-testid="input-recurring-start-time"
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">End Time</label>
              <input
                type="time"
                value={endTime}
                onChange={(e) => {
                  setEndTime(e.target.value);
                  setSuccess(null);
                }}
                data-testid="input-recurring-end-time"
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Preview */}
          {selectedDays.length > 0 && (
            <div className="bg-slate-800 rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-300 mb-2">
                Available Slots ({matchingSlots.length})
              </h4>
              {matchingSlots.length === 0 ? (
                <p className="text-gray-500 text-sm">
                  No available slots match your selection. Try different days or times.
                </p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {matchingSlots.slice(0, 10).map((slot) => (
                    <span
                      key={slot.date}
                      className="px-2 py-1 bg-emerald-500/20 text-emerald-400 text-sm rounded"
                    >
                      {slot.dayName}
                    </span>
                  ))}
                  {matchingSlots.length > 10 && (
                    <span className="px-2 py-1 text-gray-400 text-sm">
                      +{matchingSlots.length - 10} more
                    </span>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Claim button */}
          <button
            onClick={handleClaimAll}
            disabled={isSubmitting || matchingSlots.length === 0}
            data-testid="clickable-recurring-claim-submit"
            className="w-full py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting
              ? 'Claiming...'
              : matchingSlots.length === 0
              ? 'Select days and time'
              : `Claim ${matchingSlots.length} Slots (${formatTime12Hour(startTime)} - ${formatTime12Hour(endTime)})`}
          </button>
        </div>
      )}
    </div>
  );
}
